<template>
	<div class="container-fluid">
		<div class="row">
			<div class="col-12">
				<div class="box">
					<div>
						<label>{{ $t('client_area.contract_privacy.auto_invoice_fractions_title') }}</label>
						<e-select
							v-model="selected_auto_invoice_conditions"
							track-by="id"
							label="name"
							:options="auto_invoice_condition_options"
							:searchable="true"
							:multiple="true"
							:close-on-select="false"
							:show-labels="false"
							:placeholder="$t('client_area.contract_privacy.auto_invoice_fractions_placeholder')"
						>
							<template slot="noOptions">{{ $t('global.list_empty') }}</template>
						</e-select>
					</div>

					<div class="pt-3" v-if="marketplace == 'paybox' || marketplace == 'sogecommerce'">
						<b-form-checkbox v-model="contract_invoice_mode">
							{{ $t('settings.client_area.invoice_contract_mode') }}
						</b-form-checkbox>
					</div>

					<div class="pt-4">
						<b-form-checkbox v-model="contract_allow_discount">
							{{ $t('client_area.contract_privacy.allow_discount') }}
						</b-form-checkbox>
					</div>

					<div class="pt-3">
						<b-form-checkbox v-model="use_electronic_signature">
							{{ $t('client_area.contract_privacy.use_electronic_signature') }}
						</b-form-checkbox>
					</div>

					<div class="pt-3">
						<b-form-checkbox v-model="allow_payment_later">
							{{ $t('client_area.contract_privacy.allow_payment_later') }}
						</b-form-checkbox>
					</div>

					<div class="pt-4">
						<label>{{ $t('client_area.contract_privacy.allowed_countries') }}</label>
						<e-select
							v-model="allowed_countries"
							track-by="id"
							label="name"
							:options="countries_options"
							:searchable="true"
							:multiple="true"
							:close-on-select="false"
							:show-labels="false"
							:placeholder="$t('client_area.contract_privacy.allowed_countries_placeholder')"
						>
							<template slot="noOptions">{{ $t('global.list_empty') }}</template>
						</e-select>
					</div>

					<div class="pt-4">
						<label>{{ $t('client_area.contract_privacy.date_ouverture') }}</label>

						<div class="row">
							<div class="col-6">
								<div class="w-100">
									<b-input-group class="w-100" :prepend="getTrad('client_area.contract_privacy.debut')">
										<e-datepicker v-model="start_contract"></e-datepicker>
									</b-input-group>
								</div>
							</div>
							<div class="col-6">
								<div class="w-100">
									<b-input-group class="w-100" :prepend="getTrad('client_area.contract_privacy.fin')">
										<e-datepicker v-model="end_contract"></e-datepicker>
									</b-input-group>
								</div>
							</div>
						</div>

						<b-input-group :prepend="getTrad('client_area.contract_privacy.text_contract')" class="mt-2">
							<b-form-input
								class="mr-1"
								v-model="text_contract"
								:placeholder="$t('client_area.contract_privacy.text_contract')"
							></b-form-input>
						</b-input-group>
					</div>

					<div class="pt-4">
						<label>{{ $t('client_area.contract_privacy.notification_from_email') }}</label>
						<e-select
							v-model="notification_from_email"
							:options="from_email_options"
							:searchable="true"
							:taggable="true"
							:placeholder="$t('client_area.contract_privacy.notification_from_email_placeholder')"
							:show-labels="false"
							@tag="notification_from_email = $event"
						>
							<template slot="noOptions">{{ $t('global.list_empty') }}</template>
						</e-select>
					</div>

					<div class="pt-4">
						<label>{{ $t('client_area.contract_privacy.notification_to_email') }}</label>
						<b-form-input
							v-model="notification_to_email"
							type="email"
							:placeholder="$t('client_area.contract_privacy.notification_to_email_placeholder')"
						></b-form-input>
					</div>

					<div class="pt-4 border rounded p-3 bg-light">
						<label class="font-weight-bold">{{ $t('client_area.contract_privacy.test_admin_notif_email') }}</label>
						<p class="text-muted small mb-2">{{ $t('client_area.contract_privacy.test_admin_notif_email_help') }}</p>
						<div class="d-flex align-items-center">
							<b-form-input
								v-model="test_contract_id"
								type="number"
								class="mr-2"
								style="max-width: 200px;"
								:placeholder="$t('client_area.contract_privacy.test_admin_notif_contract_id')"
							></b-form-input>
							<button
								class="btn btn-outline-secondary"
								:disabled="!test_contract_id || test_sending"
								@click="sendTestAdminNotifMail"
							>
								<span v-if="test_sending">
									<b-spinner small></b-spinner>
								</span>
								<span v-else>{{ $t('client_area.contract_privacy.test_admin_notif_send') }}</span>
							</button>
						</div>
					</div>

					<div class="pt-4 border rounded p-3 bg-light mt-3">
						<label class="font-weight-bold">{{ $t('client_area.contract_privacy.test_client_notif_email') }}</label>
						<p class="text-muted small mb-2">{{ $t('client_area.contract_privacy.test_client_notif_email_help') }}</p>
						<div class="d-flex align-items-center flex-wrap">
							<b-form-input
								v-model="test_client_contract_id"
								type="number"
								class="mr-2 mb-2"
								style="max-width: 200px;"
								:placeholder="$t('client_area.contract_privacy.test_admin_notif_contract_id')"
							></b-form-input>
							<b-form-input
								v-model="test_client_email_override"
								type="email"
								class="mr-2 mb-2"
								style="max-width: 260px;"
								:placeholder="$t('client_area.contract_privacy.test_client_notif_email_override')"
							></b-form-input>
							<button
								class="btn btn-outline-secondary mb-2"
								:disabled="!test_client_contract_id || test_client_sending"
								@click="sendTestClientNotifMail"
							>
								<span v-if="test_client_sending">
									<b-spinner small></b-spinner>
								</span>
								<span v-else>{{ $t('client_area.contract_privacy.test_client_notif_send') }}</span>
							</button>
						</div>
					</div>

					<div class="mt-4 col-12">
						<div class="form-group text-center">
							<button class="btn btn-primary" :disabled="saving" @click="save">
								{{ $t('global.sauvegarder') }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script type="text/javascript">
	import _defaults from 'lodash/defaults'
	import Vue from 'vue'
	import Countries from 'GroomyRoot/assets/lang/countries/countries'
	import Contract from "@/mixins/Contract.js"
	import Marketplace from "@/mixins/Marketplace.js"

	export default {
		name: "ContractCommercializationSettings",
		mixins: [Contract, Marketplace],
		data() {
			return {
				saving: false,
				contract_allow_discount: true,
				use_electronic_signature: false,
				allow_payment_later: true,
				allowed_countries: [],
				start_contract: null,
				end_contract: null,
				text_contract: '',
				notification_from_email: '',
				notification_to_email: '',
				from_email_options: [],
				test_contract_id: '',
				test_sending: false,
				test_client_contract_id: '',
				test_client_email_override: '',
				test_client_sending: false,
				contract_invoice_mode: false,
				marketplace: null,
				auto_invoice_items: [],
				selected_auto_invoice_conditions: [],
			}
		},
		computed: {
			countries_options() {
				const locale = Vue.i18n.locale()
				const data = Countries[locale] || Countries['fr']
				const labels = data.label
				const iso3 = data.iso_3
				return Object.keys(labels)
					.map(id => ({ id: parseInt(id), name: labels[id] + ' - ' + iso3[id] }))
					.sort((a, b) => {
						const x = a.name.toLowerCase()
						const y = b.name.toLowerCase()
						return x < y ? -1 : x > y ? 1 : 0
					})
			},
			auto_invoice_condition_options() {
				return [
					{ id: 'frais_reservation', name: this.$t('monte.frais_reservation') },
					{ id: 'frais_technique', name: this.$t('monte.frais_technique') },
				]
			},
		},
		created() {
			this.init_component()
		},
		methods: {
			async init_component() {
				const [ca_config, invoice_items, ca_invoice_mode, marketplace] = await Promise.all([
					this.getCaContractPrivacyPolicy(),
					this.getConditionsMonteCaInvoice(),
					this.getCaContractInvoiceMode(),
					this.getMarketplaceTypeNotArea(),
				])

				const temp = _defaults(ca_config || {}, {
					contract_allow_discount: true,
					use_electronic_signature: false,
					allow_payment_later: true,
					allowed_countries: [],
					start_contract: null,
					end_contract: null,
					text_contract: '',
					notification_from_email: '',
					notification_to_email: '',
				})

				this.contract_allow_discount = temp.contract_allow_discount
				this.use_electronic_signature = temp.use_electronic_signature
				this.allow_payment_later = temp.allow_payment_later
				this.start_contract = temp.start_contract
				this.end_contract = temp.end_contract
				this.text_contract = temp.text_contract
				this.notification_from_email = temp.notification_from_email
				this.notification_to_email = temp.notification_to_email

				const rawCountries = temp.allowed_countries || []
				this.allowed_countries = rawCountries
					.map(id => this.countries_options.find(c => c.id == id))
					.filter(Boolean)

				this.auto_invoice_items = Array.isArray(invoice_items) ? invoice_items : []

				const enabledTypes = this.auto_invoice_items
					.filter(item => ['frais_reservation', 'frais_technique'].includes(item.contractconditions_type) && item.ca_invoice && item.ca_invoice.condcainvoice_enabled)
					.map(item => item.contractconditions_type)

				this.selected_auto_invoice_conditions = this.auto_invoice_condition_options.filter(option => enabledTypes.includes(option.id))

				this.marketplace = marketplace ? marketplace.type : null
				this.contract_invoice_mode = !!(ca_invoice_mode && ca_invoice_mode.contract_invoice_mode == 'before')

				await this.loadFromEmailOptions()
			},

			async loadFromEmailOptions() {
				const from_address = await this.loadAllFromAddress()
				if (!from_address) return

				const entries = Object.values(from_address).filter(from => from.licenceconfig_value)
				const hasValidLicenceKey = entries.filter(item => item.licenceconfig_licencekey !== null && item.licenceconfig_key === 'from_address')
				const addressEntries = hasValidLicenceKey.length > 0 ? hasValidLicenceKey : entries.filter(item => item.licenceconfig_key === 'from_address')

				const emails = []
				for (const entry of addressEntries) {
					if (entry.licenceconfig_value) {
						const values = entry.licenceconfig_value.split(',')
						for (const val of values) {
							const trimmed = val.trim()
							if (trimmed && !emails.includes(trimmed)) {
								emails.push(trimmed)
							}
						}
					}
				}

				this.from_email_options = emails
				if (this.notification_from_email && !emails.includes(this.notification_from_email)) {
					this.from_email_options.unshift(this.notification_from_email)
				}
			},

			async save() {
				this.saving = true
				try {
					const privacySettings = {
						contract_allow_discount: this.contract_allow_discount,
						use_electronic_signature: this.use_electronic_signature,
						allow_payment_later: this.allow_payment_later,
						allowed_countries: this.allowed_countries.map(c => c.id),
						start_contract: this.start_contract,
						end_contract: this.end_contract,
						text_contract: this.text_contract,
						notification_from_email: this.notification_from_email,
						notification_to_email: this.notification_to_email,
					}

					// Préparer le batch conditions : toutes les conditions ciblées avec leur état activé/désactivé
					const enabledTypes = this.selected_auto_invoice_conditions.map(item => item.id)
					const conditions = this.auto_invoice_items
						.filter(item => ['frais_reservation', 'frais_technique'].includes(item.contractconditions_type))
						.map(item => ({
							condition_id: item.contractconditions_id,
							state: enabledTypes.includes(item.contractconditions_type),
						}))

					// 2 appels parallèles : ca_settings batch + conditions batch
					await this.saveCaContractCommercializationBatch(
						privacySettings,
						this.contract_invoice_mode ? 'before' : 'after',
						conditions
					)

					this.successToast()
				} catch(e) {
					this.failureToast('Erreur lors de la sauvegarde', { error: e.message || e })
				} finally {
					this.saving = false
				}
			},

			async sendTestAdminNotifMail() {
				this.test_sending = true
				try {
					await this.sendTestCaAdminNotifMail(this.test_contract_id)
					this.successToast('client_area.contract_privacy.test_admin_notif_success')
				} catch(e) {
					this.failureToast('client_area.contract_privacy.test_notif_error', { error: e.message || e })
				} finally {
					this.test_sending = false
				}
			},

			async sendTestClientNotifMail() {
				this.test_client_sending = true
				try {
					const res = await this.sendTestCaClientNotifMail(this.test_client_contract_id, this.test_client_email_override || null)
					const sentTo = res && res.email ? res.email : ''
					if (sentTo) {
						this.successToast('client_area.contract_privacy.test_client_notif_success_to', { email: sentTo })
					} else {
						this.successToast('client_area.contract_privacy.test_client_notif_success')
					}
				} catch(e) {
					this.failureToast('client_area.contract_privacy.test_notif_error', { error: e.message || e })
				} finally {
					this.test_client_sending = false
				}
			},
		}
	}
</script>
